import React, { useEffect, useState, createContext, Suspense, useRef } from 'react';
import { io } from 'socket.io-client';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navbar from './components/UI/Navbar';
import Sidebar from './components/UI/Sidebar';
import WelcomePopup from './components/UI/WelcomePopup';
import Dashboard from './pages/Dashboard';
import GameList from './pages/GameList';
import RoomJoin from './pages/RoomJoin';
import Results from './pages/Results';
import LoadingSpinner from './components/UI/LoadingSpinner';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import toastManager from './utils/toastManager';
import Gameboard from './components/Gameboard/Gameboard';
import Play3DClient from './components/Play3D/Play3DClient';
import LoginPage from './components/LoginPage/LoginPage';
import AdminPage from './components/Admin/AdminPage';
import GameJoinPage from './components/GameJoin/GameJoinPage';
import GameModesPage from './components/GameModes/GameModesPage';
import ApiDocsPage from './components/ApiDocs/ApiDocsPage';
import GameWatchPage from './components/GameWatch/GameWatchPage';
import UserLoginPage from './components/Auth/UserLoginPage';
import ReplayPage from './pages/ReplayPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { AuthProvider, useAuth } from './context/AuthContext';
import { backendUrl } from './config/api';

export const PlayerDataContext = createContext();
export const SocketContext = createContext();


const ACTIVE_SESSION_KEY = 'ludoActiveSession';

const readStoredActiveSession = () => {
    try {
        const raw = window.localStorage.getItem(ACTIVE_SESSION_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed?.roomId || !parsed?.playerId || parsed.status === 'completed') return null;
        return parsed;
    } catch (_) {
        return null;
    }
};

const storeActiveSession = sessionData => {
    if (!sessionData?.roomId || !sessionData?.playerId) return;
    try {
        const previous = readStoredActiveSession() || {};
        window.localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({
            ...previous,
            ...sessionData,
            status: sessionData.status || previous.status || 'running',
            updatedAt: Date.now(),
        }));
    } catch (_) {}
};

const clearActiveSession = () => {
    try { window.localStorage.removeItem(ACTIVE_SESSION_KEY); } catch (_) {}
};

// Error boundary component to prevent crashes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div style={{ 
        textAlign: 'center', 
        marginTop: '20px', 
        color: 'white',
        background: 'rgba(0,0,0,0.7)',
        padding: '20px',
        borderRadius: '8px' 
      }}>
        <h2>Something went wrong.</h2>
        <p>The application encountered an error. Please refresh the page.</p>
        <button onClick={() => window.location.reload()} style={{
          padding: '10px 20px',
          background: '#4a90e2',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}>Refresh</button>
      </div>;
    }
    return this.props.children;
  }
}

function App() {
    // Hydrate the game route immediately on refresh. The backend still validates
    // this identity before reconnecting, but the UI no longer flashes Login or
    // the wrong room layout while that validation is in flight.
    const [playerData, setPlayerData] = useState(() => readStoredActiveSession() || undefined);
    const [playerSocket, setPlayerSocket] = useState();
    const [redirect, setRedirect] = useState(false);
    const [loading, setLoading] = useState(true);
    const [connectionError, setConnectionError] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const socketRef = useRef(null); // Use ref to keep track of socket
    const playerDataRef = useRef(playerData);
    const restoreInFlightRef = useRef(false);
    const lastRestoreSocketIdRef = useRef(null);
    const hadLiveConnectionRef = useRef(false);

    useEffect(() => {
        playerDataRef.current = playerData;
    }, [playerData]);
    
    useEffect(() => {
        const currentPath = window.location.pathname;
        const isSpectatorRoute = currentPath.startsWith('/watch/');
        const isJoinRoute = currentPath.startsWith('/join/');
        const isStandalone3DRoute = currentPath === '/play3d';
        const isPrivacyRoute = currentPath === '/privacy-policy';
        // This effect remains mounted while React Router moves from /join to
        // /play. Reconnect decisions must read the current URL, not the route
        // captured during the first socket connection.
        const isCurrentSpectatorRoute = () => window.location.pathname.startsWith('/watch/');
        const isCurrentJoinRoute = () => window.location.pathname.startsWith('/join/');
        // Clear stale redirect flags when app loads or refreshes
        setRedirect(false);

        // The Godot client is local/browser-side and must remain playable even
        // when the live multiplayer API is offline.
        if (isStandalone3DRoute || isPrivacyRoute) {
            setLoading(false);
            setConnectionError(false);
            setReconnecting(false);
            setPlayerSocket(undefined);
            return undefined;
        }
        
        // Cleanup existing socket if it exists (even if still mid-handshake)
        if (socketRef.current) {
            console.log('Cleaning up existing socket connection');
            socketRef.current.disconnect();
        }
        
        try {
            // Ensure we're using the correct protocol (HTTPS in production)
            const socketUrl = backendUrl;
            console.log('Connecting to socket at:', socketUrl);
            
            const socket = io(socketUrl, { 
                withCredentials: true,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 500,
                reconnectionDelayMax: 2500,
                timeout: 8000,
                forceNew: false,
                rememberUpgrade: true,
                upgrade: true,
                transports: ['websocket', 'polling']
            });
            
            socketRef.current = socket; // Store in ref for cleanup

            const finishRestore = () => {
                restoreInFlightRef.current = false;
                setReconnecting(false);
            };

            const restoreLiveSession = () => {
                if (isCurrentSpectatorRoute() || isCurrentJoinRoute()) {
                    finishRestore();
                    return;
                }
                // `connect` fires on both the first connection and every Socket.IO
                // reconnect. One restore per socket id prevents duplicate joins and
                // duplicate room:data bursts.
                if (restoreInFlightRef.current || lastRestoreSocketIdRef.current === socket.id) return;
                lastRestoreSocketIdRef.current = socket.id;
                restoreInFlightRef.current = true;

                const activePlayer = playerDataRef.current || readStoredActiveSession();
                if (!activePlayer?.roomId || !activePlayer?.playerId) {
                    socket.emit('player:check-session');
                    finishRestore();
                    return;
                }

                socket.timeout(10000).emit('player:restore-session', {
                    roomId: activePlayer.roomId,
                    playerId: activePlayer.playerId,
                    // Sent for backwards compatibility only; the server resolves
                    // authoritative color/seat from the room player record.
                    color: activePlayer.color,
                    seat: activePlayer.seat,
                }, (timeoutError, response) => {
                    if (timeoutError || !response?.success) {
                        if (response?.stale === true) {
                            clearActiveSession();
                            setPlayerData(undefined);
                        }
                        socket.emit('player:check-session');
                        finishRestore();
                        return;
                    }
                    const restoredSession = { ...activePlayer, ...(response.session || {}) };
                    storeActiveSession(restoredSession);
                    setPlayerData(previous => ({ ...(previous || {}), ...restoredSession }));
                    playerDataRef.current = { ...(playerDataRef.current || {}), ...restoredSession };
                    socket.timeout(10000).emit('player:reconnect', restoredSession.roomId, (reconnectTimeout, reconnectResponse) => {
                        if (reconnectTimeout || !reconnectResponse?.success) {
                            socket.emit('player:check-session');
                            finishRestore();
                            return;
                        }
                        const finalSession = { ...restoredSession, ...(reconnectResponse.session || {}) };
                        storeActiveSession(finalSession);
                        setPlayerData(previous => ({ ...(previous || {}), ...finalSession }));
                        playerDataRef.current = { ...(playerDataRef.current || {}), ...finalSession };
                        finishRestore();
                    });
                });
            };
            
            const handleConnect = () => {
                console.log('Socket connected:', socket.id);
                const isRecoveredConnection = hadLiveConnectionRef.current;
                hadLiveConnectionRef.current = true;
                setPlayerSocket(socket);
                setLoading(false);
                setConnectionError(false);
                if (!isRecoveredConnection) setReconnecting(false);

                // Record server URL for future auto-connect/reference
                try { window.localStorage.setItem('ludoServerUrl', socketUrl); } catch {}

                // Restore only an existing server-side game session.
                restoreLiveSession();
            };
            socket.on('connect', handleConnect);
            
            const handleDisconnect = (reason) => {
                console.log('Socket disconnected', reason);
                restoreInFlightRef.current = false;
                lastRestoreSocketIdRef.current = null;
                const hasLiveGame = Boolean(playerDataRef.current?.roomId || readStoredActiveSession()?.roomId);
                const onGameRoute = ['/play', '/game'].includes(window.location.pathname);
                // Do not show a full-screen reconnect modal on lobby pages or an
                // intentional client disconnect. Keep the board covered only when
                // a real live match loses transport.
                if (reason !== 'io client disconnect' && hasLiveGame && onGameRoute) {
                    setReconnecting(true);
                }
            };

            const handleReconnectAttempt = (attempt) => {
                const hasLiveGame = Boolean(playerDataRef.current?.roomId || readStoredActiveSession()?.roomId);
                const onGameRoute = ['/play', '/game'].includes(window.location.pathname);
                if (hasLiveGame && onGameRoute) setReconnecting(true);
                console.log('Reconnecting attempt', attempt);
            };

            const handleReconnect = (attempt) => {
                // `connect` performs the single guarded restore. Do not start a
                // second restore from the manager-level reconnect event.
                console.log('Transport reconnected after attempts', attempt);
            };

            const handleReconnectError = (err) => {
                const hasLiveGame = Boolean(playerDataRef.current?.roomId || readStoredActiveSession()?.roomId);
                const onGameRoute = ['/play', '/game'].includes(window.location.pathname);
                if (hasLiveGame && onGameRoute) setReconnecting(true);
                console.log('Reconnect error', err?.message || err);
            };

            const handleReconnectFailed = () => {
                setReconnecting(false);
                setConnectionError(true);
                console.log('Reconnect failed');
            };
            
            const handleConnectError = (err) => {
                console.error('Socket connection error:', err);
                setLoading(false);
                const hasLiveGame = Boolean(playerDataRef.current?.roomId || readStoredActiveSession()?.roomId);
                const onGameRoute = ['/play', '/game'].includes(window.location.pathname);
                if (hasLiveGame && onGameRoute) setReconnecting(true);
                // Socket.IO retries indefinitely; reserve the hard error screen for
                // manager-level reconnect failure instead of every transient error.
                setConnectionError(false);
            };
            
            const handlePlayerData = (data) => {
                if (isCurrentSpectatorRoute()) return;
                console.log('Received player:data in App.js:', data);
                try {
                    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
                    playerDataRef.current = parsedData;
                    setPlayerData(parsedData);
                    if (parsedData?.roomId && parsedData?.playerId) storeActiveSession(parsedData);
                    
                    // Explicitly set redirect to true when valid player data is received
                    if (parsedData && parsedData.roomId) {
                        console.log("Setting redirect flag to true - valid roomId found in player data");
                        setRedirect(true);
                    }
                } catch (err) {
                    console.error('Error parsing player data:', err);
                    toastManager.error('Error processing player data');
                }
            };

            const handleActiveSession = sessionData => {
                if (isCurrentSpectatorRoute() || isCurrentJoinRoute()) return;
                if (!sessionData?.roomId || !sessionData?.playerId || sessionData.status === 'completed') {
                    if (!sessionData || sessionData.status === 'completed') clearActiveSession();
                    return;
                }
                storeActiveSession(sessionData);
                socket.timeout(10000).emit('player:restore-session', sessionData, (timeoutError, response) => {
                    if (timeoutError || !response?.success) {
                        if (response?.stale === true) clearActiveSession();
                        return;
                    }
                    socket.timeout(10000).emit('player:reconnect', sessionData.roomId, () => {});
                });
            };

            const handleReconnectionFailed = payload => {
                console.warn('Game reconnection failed:', payload);
                clearActiveSession();
                setPlayerData(undefined);
                setRedirect(false);
                setReconnecting(false);
                if (window.location.pathname === '/play' || window.location.pathname === '/game') {
                    window.location.replace('/');
                }
            };
            
            // Add error listener for server messages
            const handleServerError = (error) => {
                // Spectator and join screens render their own expected room/link
                // errors. Do not duplicate them as a global console/toast error.
                if (isCurrentSpectatorRoute() || isCurrentJoinRoute()) return;
                console.error('Server error:', error);
                toastManager.error(error?.message || 'An error occurred');
            };
            
            // Add listeners for important game events
            const handleRoomJoined = () => {
                if (isCurrentSpectatorRoute()) return;
                toastManager.success('Joined game room');
                setRedirect(true);
            };
            
            const handleGameStarted = () => {
                toastManager.info('Game started!');
            };
            
            const handleYourTurn = () => {
                toastManager.info('It\'s your turn!');
            };

            socket.on('disconnect', handleDisconnect);
            socket.io.on('reconnect_attempt', handleReconnectAttempt);
            socket.io.on('reconnect', handleReconnect);
            socket.io.on('reconnect_error', handleReconnectError);
            socket.io.on('reconnect_failed', handleReconnectFailed);
            socket.on('connect_error', handleConnectError);
            socket.on('player:data', handlePlayerData);
            socket.on('player:active-session', handleActiveSession);
            socket.on('reconnection:failed', handleReconnectionFailed);
            socket.on('error', handleServerError);
            socket.on('room:joined', handleRoomJoined);
            socket.on('game:started', handleGameStarted);
            socket.on('game:yourTurn', handleYourTurn);
            
            setPlayerSocket(socket);
            
            return () => {
                console.log('Cleaning up socket connection on unmount');
                if (socket) {
                    socket.off('connect', handleConnect);
                    socket.off('disconnect', handleDisconnect);
                    socket.off('connect_error', handleConnectError);
                    socket.off('player:data', handlePlayerData);
                    socket.off('player:active-session', handleActiveSession);
                    socket.off('reconnection:failed', handleReconnectionFailed);
                    socket.off('error', handleServerError);
                    socket.off('room:joined', handleRoomJoined);
                    socket.off('game:started', handleGameStarted);
                    socket.off('game:yourTurn', handleYourTurn);
                    if (socket.io && socket.io.off) {
                        socket.io.off('reconnect_attempt', handleReconnectAttempt);
                        socket.io.off('reconnect', handleReconnect);
                        socket.io.off('reconnect_error', handleReconnectError);
                        socket.io.off('reconnect_failed', handleReconnectFailed);
                    }
                    // Always disconnect — a socket still mid-handshake would
                    // otherwise keep connecting as a ghost alongside the next
                    // socket (StrictMode double-mount), causing phantom
                    // "Reconnecting…" overlays and duplicate sessions.
                    socket.disconnect();
                }
                socketRef.current = null;
            };
        } catch (err) {
            console.error("Error setting up socket connection:", err);
            setLoading(false);
            setConnectionError(true);
            toastManager.error('Failed to initialize game connection');
        }
    }, []);

    // Allow direct access to game page if player data exists
    const canAccessGame = !!playerData && !!playerData.roomId;

    if (connectionError) {
        return (
            <div style={{ textAlign: 'center', marginTop: '50px', color: 'white' }}>
                <h2>Connection Error</h2>
                <p>Unable to connect to the game server. Please check your connection and try again.</p>
                <button onClick={() => window.location.reload()} style={{
                    padding: '10px 20px',
                    background: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    marginTop: '20px',
                    cursor: 'pointer'
                }}>Retry</button>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <AuthProvider>
            <SocketContext.Provider value={playerSocket}>
                <ToastContainer position="top-right" autoClose={3000} theme="dark" />
                <Suspense fallback={<LoadingSpinner color="white" height={96} width={96} />}>
                    <Router>
                        <RouteChromeWrapper>
                            {reconnecting && (
                              <div style={{position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.6)',zIndex:1000,flexDirection:'column',color:'#fff'}}>
                                <div style={{marginBottom:12,fontSize:18}}>Reconnecting to server…</div>
                                <button onClick={() => { try { playerSocket?.emit?.('player:exit'); } catch(e){} window.location.href = '/'; }} style={{padding:'10px 16px',background:'#e53935',color:'#fff',border:'none',borderRadius:6,cursor:'pointer'}}>Exit</button>
                              </div>
                            )}
                            <AnimatePresence mode="wait">
                              <Routes>
                                <Route
                                    exact
                                    path='/'
                                    element={
                                        redirect ? 
                                        <Navigate to='/play' replace /> : 
                                        loading ? 
                                        <LoadingSpinner color="white" height={96} width={96} /> : 
                                        <LoginPage />
                                    }
                                />
                                <Route
                                    path='/login'
                                    element={
                                        redirect ? 
                                        <Navigate to='/play' replace /> : 
                                        loading ? 
                                        <LoadingSpinner color="white" height={96} width={96} /> : 
                                        <LoginPage />
                                    }
                                />
                                <Route
                                    path='/play'
                                    element={
                                        canAccessGame ?
                                        <PlayerDataContext.Provider value={playerData}>
                                            <div className="flex items-center justify-center min-h-screen w-screen">
                                                <Gameboard />
                                            </div>
                                        </PlayerDataContext.Provider> :
                                        <Navigate to='/login' replace />
                                    }
                                />
                                <Route path='/game' element={<Navigate to='/play' replace />} />
                                <Route
                                    path='/play3d'
                                    element={
                                        <PlayerDataContext.Provider value={playerData || null}>
                                            <Play3DClient />
                                        </PlayerDataContext.Provider>
                                    }
                                />
                                {/* Themed sample UI routes */}
                                <Route path='/ui/dashboard' element={<RequireLogin><Dashboard /></RequireLogin>} />
                                <Route path='/ui/games' element={<RequireLogin><GameList /></RequireLogin>} />
                                <Route path='/ui/room' element={<RequireLogin><RoomJoin /></RequireLogin>} />
                                <Route path='/ui/results' element={<RequireLogin><Results /></RequireLogin>} />
                                <Route path='/admincp/admin' element={<RequireAdmin><AdminPage /></RequireAdmin>} />
                                <Route path='/auth/login' element={<UserLoginPage />} />
                                <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                                <Route path='/game-modes' element={<GameModesPage />} />
                                <Route path='/api-docs' element={<RequireLogin><ApiDocsPage /></RequireLogin>} />
                                <Route path='/watch/:roomId' element={<GameWatchPage />} />
                                <Route path='/replay/:roomId' element={<RequireApiManager><ReplayPage /></RequireApiManager>} />
                                <Route
                                    path='/join/:roomId'
                                    element={<GameJoinPage />}
                                />
                              </Routes>
                            </AnimatePresence>
                        </RouteChromeWrapper>
                    </Router>
                </Suspense>
            </SocketContext.Provider>
            </AuthProvider>
        </ErrorBoundary>
    );
}

// Show Navbar/Sidebar/Welcome on all lobby/UI routes (not on game/admin/join)
function RouteChromeWrapper({ children }) {
  const location = useLocation();
  const lobbyRoutes = ['/', '/login', '/game-modes'];
  const isLobbyRoute =
    lobbyRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/ui');
  const isGameRoute = ['/game', '/play', '/play3d'].includes(location.pathname);

  useEffect(() => {
    if (isGameRoute) {
      document.body.classList.add('game-page');
    } else {
      document.body.classList.remove('game-page');
    }
    return () => document.body.classList.remove('game-page');
  }, [isGameRoute]);

  return (
    <>
      {isLobbyRoute && (
        <>
          <Navbar />
          <Sidebar />
          <WelcomePopup duration={3500} />
        </>
      )}
      {children}
    </>
  );
}

function RequireLogin({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  return isLoggedIn ? children : <Navigate to="/auth/login" replace />;
}

function RequireAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin ? children : <Navigate to="/auth/login" replace />;
}

function RequireApiManager({ children }) {
  const { isApiManager, loading } = useAuth();
  if (loading) return null;
  return isApiManager ? children : <Navigate to="/auth/login" replace />;
}

export default App;
